import { useEffect } from 'react';
import { useActorRef, useSelector } from '@xstate5/react';
import type { MachineImplementationsFrom } from 'xstate5';

import { customizationMachine } from './customization.machine';
import { customizationLogger } from './helpers';
import { useFetchCustomizationDataActor } from './hooks';
import { type Input } from './types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initializes and provides a reference to a customization state machine actor.
 */
export const useInitCustomizationMachine = (
  input: Input,
  implementations?: Implementations,
) => {
  const fetchCustomizationData = useFetchCustomizationDataActor();

  // ─── Actor ───────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    customizationMachine.provide({
      ...implementations,
      actors: {
        ...implementations?.actors,
        fetchCustomizationData,
      },
      guards: {
        ...implementations?.guards,
      },
    }),
    { input },
  );

  const currentState = useSelector(actorRef, (state) => state.value);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    customizationLogger.debug(currentState);
  }, [currentState]);

  // ─────────────────────────────────────────────────────────────────────

  return actorRef;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Implementations = MachineImplementationsFrom<typeof customizationMachine>;
